import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  CubeIcon,
  TagIcon,
  ShoppingCartIcon,
  ArrowTrendingDownIcon,
  ArrowUpIcon,
  ArrowDownIcon,
  AdjustmentsHorizontalIcon,
} from '@heroicons/react/24/outline';

export default function Dashboard() {
  const [statsData, setStatsData] = useState({
    totalInventory: 0,
    lowStock: 0,
    categories: 0,
    recentPurchases: 0,
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [recentActivities, setRecentActivities] = useState([]);
  const [inventoryChanges, setInventoryChanges] = useState([]);

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      setError(null);

      // Fetch inventory stats with proper aggregation
      const inventoryResponse = await fetch('/api/inventory/stats', {
        headers: {
          'X-Employee-ID': localStorage.getItem('employeeId'),
        },
      });
      const inventoryStats = await inventoryResponse.json();

      // Fetch categories
      const categoriesResponse = await fetch('/api/categories', {
        headers: {
          'X-Employee-ID': localStorage.getItem('employeeId'),
        },
      });
      const categoriesData = await categoriesResponse.json();

      // Fetch recent purchases with proper date range
      const today = new Date();
      const thirtyDaysAgo = new Date(today.setDate(today.getDate() - 30));
      const purchasesResponse = await fetch(
        `/api/purchase-records/stats?startDate=${thirtyDaysAgo.toISOString()}`, 
        {
          headers: {
            'X-Employee-ID': localStorage.getItem('employeeId'),
          },
        }
      );
      const purchasesStats = await purchasesResponse.json();

      setStatsData({
        totalInventory: inventoryStats.total || 0,
        lowStock: inventoryStats.lowStock || 0,
        categories: categoriesData.categories?.length || 0,
        recentPurchases: purchasesStats.total || 0,
      });

      // Fetch recent activities
      const recentActivitiesResponse = await fetch('/api/purchase-records?limit=5&sort=-purchase_date', {
        headers: {
          'X-Employee-ID': localStorage.getItem('employeeId'),
        },
      });
      const recentActivitiesData = await recentActivitiesResponse.json();
      setRecentActivities(recentActivitiesData.records || []);

      // Fetch recent inventory changes
      const changesResponse = await fetch('/api/inventory/changes?limit=5', {
        headers: {
          'X-Employee-ID': localStorage.getItem('employeeId'),
        },
      });
      const changesData = await changesResponse.json();
      setInventoryChanges(changesData.changes || []);
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
      setError('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  const stats = [
    {
      name: 'Total Inventory',
      value: statsData.totalInventory,
      icon: CubeIcon,
      href: '/inventory',
      color: 'bg-blue-500',
    },
    {
      name: 'Categories',
      value: statsData.categories,
      icon: TagIcon,
      href: '/categories',
      color: 'bg-green-500',
    },
    {
      name: 'Recent Purchases',
      value: statsData.recentPurchases,
      icon: ShoppingCartIcon,
      href: '/purchase-records',
      color: 'bg-purple-500',
    },
    {
      name: 'Low Stock Items',
      value: statsData.lowStock,
      icon: ArrowTrendingDownIcon,
      href: '/inventory?filter=low-stock',
      color: 'bg-red-500',
    },
  ];

  const getChangeTypeIcon = (type) => {
    switch (type) {
      case 'purchase':
        return <ShoppingCartIcon className="h-5 w-5 text-green-500" />;
      case 'sale':
        return <ArrowDownIcon className="h-5 w-5 text-red-500" />;
      case 'manual':
        return <ArrowUpIcon className="h-5 w-5 text-blue-500" />;
      case 'adjustment':
        return <AdjustmentsHorizontalIcon className="h-5 w-5 text-yellow-500" />;
      default:
        return null;
    }
  };

  const getChangeTypeColor = (type) => {
    switch (type) {
      case 'purchase':
        return 'bg-green-100 text-green-800';
      case 'sale':
        return 'bg-red-100 text-red-800';
      case 'manual':
        return 'bg-blue-100 text-blue-800';
      case 'adjustment':
        return 'bg-yellow-100 text-yellow-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  if (loading) {
    return (
      <div className="animate-pulse">
        <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
          {[...Array(4)].map((_, i) => (
            <div key={i} className="bg-white overflow-hidden shadow rounded-lg">
              <div className="p-5">
                <div className="h-8 bg-gray-200 rounded w-1/2 mb-4"></div>
                <div className="h-6 bg-gray-200 rounded"></div>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="rounded-md bg-red-50 p-4">
        <div className="flex">
          <div className="ml-3">
            <h3 className="text-sm font-medium text-red-800">{error}</h3>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="mb-6">
        <h1 className="text-2xl font-semibold text-gray-900">Dashboard</h1>
      </div>

      <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
        {stats.map((item) => (
          <Link
            key={item.name}
            to={item.href}
            className="bg-white overflow-hidden shadow rounded-lg hover:shadow-md transition-shadow duration-200"
          >
            <div className="p-5">
              <div className="flex items-center">
                <div className={`flex-shrink-0 rounded-md p-3 ${item.color}`}>
                  <item.icon className="h-6 w-6 text-white" aria-hidden="true" />
                </div>
                <div className="ml-5 w-0 flex-1">
                  <dl>
                    <dt className="text-sm font-medium text-gray-500 truncate">{item.name}</dt>
                    <dd className="flex items-baseline">
                      <div className="text-2xl font-semibold text-gray-900">{item.value}</div>
                    </dd>
                  </dl>
                </div>
              </div>
            </div>
          </Link>
        ))}
      </div>

      <div className="mt-8 grid grid-cols-1 gap-8 lg:grid-cols-2">
        {/* Recent Activities */}
        <div>
          <h2 className="text-lg font-medium text-gray-900 mb-4">Recent Purchases</h2>
          <div className="bg-white shadow overflow-hidden sm:rounded-md">
            <ul role="list" className="divide-y divide-gray-200">
              {recentActivities.map((activity) => (
                <li key={activity.id}>
                  <div className="px-4 py-4 sm:px-6">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center">
                        <ShoppingCartIcon className="h-5 w-5 text-gray-400 mr-3" />
                        <p className="text-sm font-medium text-blue-600 truncate">
                          {activity.inventory.name}
                        </p>
                      </div>
                      <div className="ml-2 flex-shrink-0 flex">
                        <p className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-green-100 text-green-800">
                          Purchased
                        </p>
                      </div>
                    </div>
                    <div className="mt-2 sm:flex sm:justify-between">
                      <div className="sm:flex">
                        <p className="flex items-center text-sm text-gray-500">
                          Quantity: {activity.quantity} {activity.inventory.unit}
                        </p>
                      </div>
                      <div className="mt-2 flex items-center text-sm text-gray-500 sm:mt-0">
                        <p>
                          {new Date(activity.purchase_date).toLocaleDateString()}
                        </p>
                      </div>
                    </div>
                  </div>
                </li>
              ))}
              {recentActivities.length === 0 && (
                <li className="px-4 py-5 text-center text-sm text-gray-500">
                  No recent purchases
                </li>
              )}
            </ul>
          </div>
        </div>

        {/* Inventory Changes */}
        <div>
          <h2 className="text-lg font-medium text-gray-900 mb-4">Inventory Changes</h2>
          <div className="bg-white shadow overflow-hidden sm:rounded-md">
            <ul role="list" className="divide-y divide-gray-200">
              {inventoryChanges.map((change) => (
                <li key={change.id}>
                  <div className="px-4 py-4 sm:px-6">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center">
                        {getChangeTypeIcon(change.change_type)}
                        <p className="ml-3 text-sm font-medium text-gray-900 truncate">
                          {change.inventory.name}
                        </p>
                      </div>
                      <div className="ml-2 flex-shrink-0 flex">
                        <p className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${getChangeTypeColor(change.change_type)}`}>
                          {change.change_type.charAt(0).toUpperCase() + change.change_type.slice(1)}
                        </p>
                      </div>
                    </div>
                    <div className="mt-2 sm:flex sm:justify-between">
                      <div className="sm:flex space-x-6">
                        <p className="flex items-center text-sm text-gray-500">
                          Change: {change.quantity_change > 0 ? '+' : ''}{change.quantity_change} {change.inventory.unit}
                        </p>
                        <p className="flex items-center text-sm text-gray-500">
                          New Total: {change.new_quantity} {change.inventory.unit}
                        </p>
                      </div>
                      <div className="mt-2 flex items-center text-sm text-gray-500 sm:mt-0">
                        <p>
                          {new Date(change.created_at).toLocaleDateString()}
                        </p>
                      </div>
                    </div>
                    {change.notes && (
                      <div className="mt-2">
                        <p className="text-sm text-gray-500">{change.notes}</p>
                      </div>
                    )}
                  </div>
                </li>
              ))}
              {inventoryChanges.length === 0 && (
                <li className="px-4 py-5 text-center text-sm text-gray-500">
                  No recent changes
                </li>
              )}
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
} 